import { UserRole } from '../../types';
import ResourcesIds from '../../resources/ResourcesIds';

/**
 * A patient's record: the evaluations taken at the clinic and the documents they brought in,
 * as the one list `GET /medical-records` answers with.
 */
export type MedicalRecordKind = 'EVALUATION' | 'DOCUMENT';

/** What kind of record a patient brought in. A fixed list; the API refuses anything else. */
export type MedicalDocumentCategory =
    | 'IMAGING'
    | 'LAB_RESULTS'
    | 'REFERRAL'
    | 'DISCHARGE_SUMMARY'
    | 'SURGERY_REPORT'
    | 'OTHER';

/** In the order the upload form offers them: commonest first, the catch-all last. */
export const MEDICAL_DOCUMENT_CATEGORIES: MedicalDocumentCategory[] = [
    'IMAGING',
    'LAB_RESULTS',
    'REFERRAL',
    'DISCHARGE_SUMMARY',
    'SURGERY_REPORT',
    'OTHER',
];

const CATEGORY_LABELS: Record<MedicalDocumentCategory, string> = {
    IMAGING: ResourcesIds.MEDICAL_DOCUMENT_CATEGORY_IMAGING,
    LAB_RESULTS: ResourcesIds.MEDICAL_DOCUMENT_CATEGORY_LAB_RESULTS,
    REFERRAL: ResourcesIds.MEDICAL_DOCUMENT_CATEGORY_REFERRAL,
    DISCHARGE_SUMMARY: ResourcesIds.MEDICAL_DOCUMENT_CATEGORY_DISCHARGE_SUMMARY,
    SURGERY_REPORT: ResourcesIds.MEDICAL_DOCUMENT_CATEGORY_SURGERY_REPORT,
    OTHER: ResourcesIds.MEDICAL_DOCUMENT_CATEGORY_OTHER,
};

export const categoryLabelKey = (category: MedicalDocumentCategory): string => CATEGORY_LABELS[category];

/**
 * One line of the record. `kind` says which of the two it is, and the fields that belong to
 * the other kind arrive as null.
 *
 * `id` means nothing without `kind`: evaluations and documents are numbered separately, so
 * evaluation 4 and document 4 are both real. Use `recordKey` wherever a line needs a key.
 */
export interface IMedicalRecordItem {
    kind: MedicalRecordKind;
    id: number;
    /** When the evaluation was taken, or the date on the document. What the list sorts by. */
    date: string;
    /** When it was recorded here; clinic wall-clock, no zone. */
    createdAt: string;
    /** The therapist who took the evaluation, or whoever uploaded the document. */
    authorUserId: number | null;
    authorName: string | null;

    // An evaluation's.
    reasonForVisit: string | null;
    painLevel: number | null;
    pdfAvailable: boolean | null;

    // A document's.
    category: MedicalDocumentCategory | null;
    title: string | null;
    contentType: string | null;
    sizeBytes: number | null;
    originalFilename: string | null;
    /**
     * Whether whoever uploaded this may still withdraw it themselves. Decided by the API
     * against its own clock, which is why this is a flag and not a time to compare with the
     * browser's: the two clocks need not agree, and the API is the one that refuses.
     */
    removableByUploader: boolean | null;
}

export const recordKey = (item: IMedicalRecordItem): string => `${item.kind}:${item.id}`;

/** A document as the API answers an upload with. */
export interface IMedicalDocument {
    id: number;
    patientUserId: number;
    category: MedicalDocumentCategory;
    title: string;
    issuedOn: string;
    originalFilename: string;
    contentType: string;
    sizeBytes: number;
}

/** The JSON half of an upload; the files travel beside it. */
export interface IUploadMedicalDocumentsRequest {
    patientId: number;
    category: MedicalDocumentCategory;
    /** The date on the documents, as an ISO date. */
    issuedOn: string;
    /** One per file, in the order the files are sent. */
    titles: string[];
}

export interface UploadMedicalDocumentsVariables {
    request: IUploadMedicalDocumentsRequest;
    files: File[];
    /** 0 to 1, as the bytes leave the browser. */
    onProgress?: (fraction: number) => void;
}

// ---------------------------------------------------------------------------------------
// The limits. Each is the API's own (`MedicalRecordService`), restated so that a file is
// turned away the moment it is chosen rather than after it has been uploaded. Change them
// together.

export const MAX_DOCUMENT_BYTES: number = 20 * 1024 * 1024;

export const MAX_DOCUMENTS_PER_UPLOAD: number = 10;

export const MAX_UPLOAD_TOTAL_BYTES: number = 100 * 1024 * 1024;

export const DOCUMENT_TITLE_MAX: number = 200;

/**
 * What a document may be. The API decides by the file's own bytes and will refuse anything
 * else whatever it is called; this is the same list for the picker and for what the app is
 * willing to open in a tab.
 */
export const ACCEPTED_DOCUMENT_TYPES: string[] = ['application/pdf', 'image/jpeg', 'image/png'];

export const isAcceptedDocumentFile = (file: File): boolean => ACCEPTED_DOCUMENT_TYPES.includes(file.type);

/**
 * What the camera button asks for. JPEG by name rather than `image/*`, for the reason the
 * picker lists its three: naming it is what makes iOS hand over a JPEG for what it shot as
 * HEIC, which the API would refuse.
 */
export const CAMERA_CAPTURE_TYPE: string = 'image/jpeg';

const twoDigits = (value: number): string => String(value).padStart(2, '0');

/**
 * A name for a photograph taken from the dialog: "foto-20261002-143205.jpg".
 *
 * A phone calls every capture the same thing -- iOS hands over "image.jpg" each time -- so
 * three pages of one report would be three rows nobody could tell apart, here and in what
 * the record keeps as the original name. The moment it was taken is the one thing that
 * differs between them.
 */
export const photoFilename = (takenAt: Date, type: string): string => {
    const day: string = `${takenAt.getFullYear()}${twoDigits(takenAt.getMonth() + 1)}${twoDigits(takenAt.getDate())}`;
    const time: string = `${twoDigits(takenAt.getHours())}${twoDigits(takenAt.getMinutes())}${twoDigits(takenAt.getSeconds())}`;

    return `foto-${day}-${time}.${type === 'image/png' ? 'png' : 'jpg'}`;
};

/**
 * A starting title for a file: its name without the folder it came from or its extension.
 * "RMN genunchi.pdf" reads as a title already; "IMG_0042.jpeg" at least says which photo.
 */
export const titleFromFilename = (name: string): string => {
    const lastSegment: string = name.split(/[\\/]/).pop() ?? '';
    const dot: number = lastSegment.lastIndexOf('.');
    // A leading dot is a hidden file's name, not an extension.
    const stem: string = dot > 0 ? lastSegment.slice(0, dot) : lastSegment;

    return stem.trim().slice(0, DOCUMENT_TITLE_MAX);
};

/** "3.4 MB" / "512 KB", for the line beside a chosen file. */
export const formatFileSize = (bytes: number): string =>
    bytes >= 1024 * 1024
        ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Whether the viewer may withdraw a document -- the API's rule, restated so the button is
 * only drawn for somebody it will work for: an administrator always, and whoever uploaded
 * it while the API says their window is still open.
 *
 * Presentation only. The API re-checks on the request, and a page left open past the window
 * simply gets its refusal, in words.
 */
export const canRemoveDocument = (
    item: IMedicalRecordItem,
    role: UserRole | null,
    viewerId: number | undefined
): boolean => {
    if (item.kind !== 'DOCUMENT') {
        return false;
    }

    if (role === 'ADMIN') {
        return true;
    }

    return viewerId !== undefined && item.authorUserId === viewerId && item.removableByUploader === true;
};
